import { CalendarDays, ChevronLeft, ChevronRight, Clock, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDismiss } from "@/shared/hooks/useDismiss";

const WEEKDAYS = ["D", "S", "T", "Q", "Q", "S", "S"];
const MONTHS = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
];

const pad = (n: number) => String(n).padStart(2, "0");
const isoDay = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function parse(value: string): { day: string | null; hour: number; minute: number } {
  if (!value) return { day: null, hour: 0, minute: 0 };
  const [day, time] = value.split("T");
  const [h, m] = (time ?? "").split(":").map(Number);
  return {
    day: /^\d{4}-\d{2}-\d{2}$/.test(day ?? "") ? day : null,
    hour: Number.isFinite(h) ? Math.min(23, Math.max(0, h)) : 0,
    minute: Number.isFinite(m) ? Math.min(59, Math.max(0, m)) : 0
  };
}

function formatDisplay(value: string, withTime: boolean): string {
  if (!value) return "";
  const [day, time] = value.split("T");
  if (!day) return "";
  const parts = day.split("-");
  if (parts.length !== 3) return value;
  const [yyyy, mm, dd] = parts;
  const dateFormatted = `${dd}/${mm}/${yyyy}`;
  if (!withTime) return dateFormatted;
  const timeFormatted = time ? time.slice(0, 5) : "00:00";
  return `${dateFormatted}, ${timeFormatted}`;
}

export function DateField({
  value,
  onValue,
  withTime = false,
  ariaLabel,
  name,
  min,
  max,
  defaultTime = "00:00",
  align = "left",
  placeholder
}: {
  value: string;
  onValue: (value: string) => void;
  withTime?: boolean;
  ariaLabel?: string;
  name?: string;
  min?: string;
  max?: string;
  defaultTime?: string;
  align?: "left" | "right";
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  useDismiss(open, () => setOpen(false), { ref: rootRef });

  const current = parse(value);
  const today = isoDay(new Date());

  const [view, setView] = useState(() => {
    const base = current.day ? new Date(`${current.day}T00:00`) : new Date();
    return { year: base.getFullYear(), month: base.getMonth() };
  });

  const [tempHour, setTempHour] = useState(pad(current.day ? current.hour : Number(defaultTime.slice(0, 2))));
  const [tempMin, setTempMin] = useState(pad(current.day ? current.minute : Number(defaultTime.slice(3, 5))));

  useEffect(() => {
    if (!open) return;
    const base = current.day ? new Date(`${current.day}T00:00`) : new Date();
    setView({ year: base.getFullYear(), month: base.getMonth() });
    setTempHour(pad(current.day ? current.hour : Number(defaultTime.slice(0, 2))));
    setTempMin(pad(current.day ? current.minute : Number(defaultTime.slice(3, 5))));
  }, [open, value, defaultTime]);

  const cells = useMemo(() => {
    const first = new Date(view.year, view.month, 1);
    const start = new Date(first);
    start.setDate(1 - first.getDay());
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return { iso: isoDay(d), day: d.getDate(), inMonth: d.getMonth() === view.month };
    });
  }, [view]);

  const minDay = min?.slice(0, 10);
  const maxDay = max?.slice(0, 10);

  const emit = (day: string, hh: string, mm: string) => {
    const h = pad(Math.min(23, Math.max(0, Number(hh) || 0)));
    const m = pad(Math.min(59, Math.max(0, Number(mm) || 0)));
    onValue(withTime ? `${day}T${h}:${m}` : day);
  };

  const shiftMonth = (delta: number) => {
    setView(v => {
      const d = new Date(v.year, v.month + delta, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });
  };

  const handleSelectDay = (dayIso: string) => {
    emit(dayIso, tempHour, tempMin);
    if (!withTime) {
      setOpen(false);
    }
  };

  const handleApplyPreset = (hh: string, mm: string) => {
    setTempHour(hh);
    setTempMin(mm);
    const activeDay = current.day || today;
    emit(activeDay, hh, mm);
  };

  const formattedDisplay = formatDisplay(value, withTime);
  const displayPlaceholder = placeholder || (withTime ? "DD/MM/AAAA, HH:mm" : "DD/MM/AAAA");

  return (
    <div className={`date-field${open ? " is-open" : ""}`} ref={rootRef}>
      {name && <input type="hidden" name={name} value={value} />}
      <button
        type="button"
        className="date-field-trigger"
        aria-label={ariaLabel ? `${ariaLabel}: ${formattedDisplay || "não definida"}` : "Selecionar data"}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
      >
        <span className="date-field-trigger-content">
          <CalendarDays size={16} className="date-field-icon" aria-hidden="true" />
          {formattedDisplay ? (
            <span className="date-field-value">{formattedDisplay}</span>
          ) : (
            <span className="date-field-placeholder">{displayPlaceholder}</span>
          )}
        </span>
        {value && (
          <span
            className="date-field-clear-btn"
            role="button"
            tabIndex={0}
            aria-label="Limpar campo"
            onClick={e => {
              e.stopPropagation();
              onValue("");
            }}
            onKeyDown={e => {
              if (e.key === "Enter" || e.key === " ") {
                e.stopPropagation();
                onValue("");
              }
            }}
          >
            <X size={14} />
          </span>
        )}
      </button>

      {open && (
        <div
          className={`date-field-pop align-${align}`}
          role="dialog"
          aria-label={ariaLabel ?? "Calendário"}
          onClick={e => e.stopPropagation()}
        >
          <div className="date-field-head">
            <button
              type="button"
              className="date-field-nav-btn"
              aria-label="Mês anterior"
              onClick={() => shiftMonth(-1)}
            >
              <ChevronLeft size={16} />
            </button>
            <span className="date-field-title">
              {MONTHS[view.month]} de {view.year}
            </span>
            <button
              type="button"
              className="date-field-nav-btn"
              aria-label="Próximo mês"
              onClick={() => shiftMonth(1)}
            >
              <ChevronRight size={16} />
            </button>
          </div>

          <div className="date-field-grid" role="grid">
            {WEEKDAYS.map((w, i) => (
              <span key={i} className="date-field-weekday" aria-hidden="true">
                {w}
              </span>
            ))}
            {cells.map(cell => {
              const disabled = (minDay && cell.iso < minDay) || (maxDay && cell.iso > maxDay);
              const selected = cell.iso === current.day;
              return (
                <button
                  key={cell.iso}
                  type="button"
                  disabled={!!disabled}
                  className={[
                    "date-field-day",
                    cell.inMonth ? "" : "is-outside",
                    cell.iso === today ? "is-today" : "",
                    selected ? "is-selected" : ""
                  ].join(" ")}
                  aria-pressed={selected}
                  onClick={() => handleSelectDay(cell.iso)}
                >
                  {cell.day}
                </button>
              );
            })}
          </div>

          {withTime && (
            <div className="date-field-time-box">
              <div className="date-field-time-row">
                <span className="date-field-time-label">
                  <Clock size={13} aria-hidden="true" />
                  Horário
                </span>
                <div className="date-field-time-inputs">
                  <input
                    type="text"
                    maxLength={2}
                    className="date-field-time-input"
                    aria-label="Hora"
                    value={tempHour}
                    onChange={e => {
                      const v = e.target.value.replace(/\D/g, "");
                      setTempHour(v);
                      if (current.day && v.length === 2) emit(current.day, v, tempMin);
                    }}
                    onBlur={() => {
                      const h = pad(Math.min(23, Number(tempHour) || 0));
                      setTempHour(h);
                      if (current.day) emit(current.day, h, tempMin);
                    }}
                  />
                  <span className="date-field-time-colon">:</span>
                  <input
                    type="text"
                    maxLength={2}
                    className="date-field-time-input"
                    aria-label="Minuto"
                    value={tempMin}
                    onChange={e => {
                      const v = e.target.value.replace(/\D/g, "");
                      setTempMin(v);
                      if (current.day && v.length === 2) emit(current.day, tempHour, v);
                    }}
                    onBlur={() => {
                      const m = pad(Math.min(59, Number(tempMin) || 0));
                      setTempMin(m);
                      if (current.day) emit(current.day, tempHour, m);
                    }}
                  />
                </div>
              </div>

              <div className="date-field-time-presets">
                <button
                  type="button"
                  className={`date-field-preset-chip${tempHour === "00" && tempMin === "00" ? " is-active" : ""}`}
                  onClick={() => handleApplyPreset("00", "00")}
                >
                  00:00
                </button>
                <button
                  type="button"
                  className={`date-field-preset-chip${tempHour === "12" && tempMin === "00" ? " is-active" : ""}`}
                  onClick={() => handleApplyPreset("12", "00")}
                >
                  12:00
                </button>
                <button
                  type="button"
                  className={`date-field-preset-chip${tempHour === "23" && tempMin === "59" ? " is-active" : ""}`}
                  onClick={() => handleApplyPreset("23", "59")}
                >
                  23:59
                </button>
              </div>
            </div>
          )}

          <div className="date-field-foot">
            <button
              type="button"
              className="date-field-foot-btn"
              onClick={() => {
                onValue("");
                setOpen(false);
              }}
            >
              Limpar
            </button>
            <button
              type="button"
              className="date-field-foot-btn is-today-btn"
              onClick={() => {
                emit(today, tempHour, tempMin);
                setView({ year: new Date().getFullYear(), month: new Date().getMonth() });
                if (!withTime) setOpen(false);
              }}
            >
              Hoje
            </button>
            {withTime && (
              <button
                type="button"
                className="date-field-foot-btn is-primary"
                onClick={() => setOpen(false)}
              >
                Pronto
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
